import {useCallback} from 'react';

import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {isSectionPanelsAuto} from '../../../state/views/compress';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../../util/panelbankTypes';
import {usePanelIsActive} from '../../../util/usePanelIsActive';
import {useWorkspaceLayoutSettings} from '../../WorkspaceDrawer/Settings/hooks';
import {type SectionWithRef} from '../types';
import {getIsSectionNameSearchMatch} from './useSectionNameMatch';

/**
 * This hook determines if a section should be visible in the workspace based
 * on search and filtering criteria. If a section should not be rendered
 * by default, that should be handled outside of the PanelBankSection component.
 */
export const useIsSectionVisible = () => {
  const workspaceLayout = useWorkspaceLayoutSettings();
  const panelIsActive = usePanelIsActive();
  const {
    debouncedHideSearchRegex: hideSearchRegex,
    debouncedSearchRegex: searchRegex,
    isHiding,
    isSearching,
  } = usePanelSearchDebouncedContext();

  // Note: order of checks matter here!
  return useCallback(
    (sectionWithRef: SectionWithRef) => {
      // showEmptySections is a user workspace setting that applies ONLY when user is doing a **search**
      const shouldShowEmptySectionsDuringSearch =
        workspaceLayout.showEmptySections && (isSearching || isHiding);

      if (shouldShowEmptySectionsDuringSearch) {
        return true;
      }

      const hasActivePanel = (regex?: RegExp | null) =>
        sectionWithRef.panels.some(panel =>
          panelIsActive({
            section: sectionWithRef,
            panel,
            searchRegex: regex,
            hideSearchRegex,
          })
        );

      // Edge case: sections with no panels might be newly-created by the user.
      // We want to show these sections so panels can be dragged into them.
      // Checked before the section-name-match branch so newly-created empty
      // sections still surface even when the user is searching.
      if (
        sectionWithRef.panels.length === 0 &&
        !isSectionPanelsAuto(sectionWithRef, workspaceLayout)
      ) {
        return true;
      }

      // When searching, also keep sections whose name matches the search regex.
      // Downstream rendering (see `getSectionFilteredPanels`) shows all active
      // panels in such sections, so they need to survive this visibility filter.
      // However, if there are no active panels at all, the section would render
      // empty — so require at least one active panel before keeping it.
      // Note: we intentionally check `panelIsActive` WITHOUT `searchRegex` here
      // because the downstream renderer also ignores the search regex for
      // name-matched sections.
      if (getIsSectionNameSearchMatch(sectionWithRef.name, searchRegex)) {
        return hasActivePanel();
      }

      return hasActivePanel(searchRegex);
    },
    [
      hideSearchRegex,
      isHiding,
      isSearching,
      panelIsActive,
      searchRegex,
      workspaceLayout,
    ]
  );
};

export const getIsHiddenPanelsSection = ({
  idx,
  numSections,
  sectionName,
}: {
  /**
   * Index of the section in the panel bank
   */
  idx: number;
  /**
   * Total number of sections that could be rendered
   */
  numSections: number;
  sectionName: string;
}) => {
  const isLastSection = idx === numSections - 1;
  const isHiddenPanelsSection =
    isLastSection && sectionName === PANEL_BANK_HIDDEN_SECTION_NAME;

  return isHiddenPanelsSection;
};
